import debounce from 'lodash/debounce';

import { LIVE_FILTER_DEBOUNCE } from './FilterForm/FilterForm';

/**
 * Session of the mobile filters panel (D39). Like a live desktop popup (D25):
 * - changes are debounced (LIVE_FILTER_DEBOUNCE), so arrowing through radios is one search;
 * - the first applied change of a session adds a history entry, the following ones replace it,
 *   so one Back returns to the state before the panel was opened.
 * Unlike a popup, closing with ✕ «Отмена» or Esc reverts the session (the panel exception to D25):
 * revert() cancels a pending change and tells whether the session added an entry to go back from.
 *
 * @param {Function} apply called with (updatedURLParams, { replace })
 * @returns {Object} session API: start, change, keep, cancel, revert, nextEntryReplaces
 */
export const createFilterPanelSession = apply => {
  let hasEntry = false;
  let pending = null;

  const applyPending = () => {
    const params = pending;
    pending = null;
    if (!params) {
      return;
    }
    const replace = hasEntry;
    hasEntry = true;
    apply(params, { replace });
  };
  const debouncedApply = debounce(applyPending, LIVE_FILTER_DEBOUNCE);

  const cancel = () => {
    debouncedApply.cancel();
    pending = null;
  };

  return {
    // Panel opened
    start: () => {
      cancel();
      hasEntry = false;
    },
    // A filter in the panel changed (several filters in one burst are merged)
    change: params => {
      pending = { ...pending, ...params };
      debouncedApply();
    },
    // «Показать N результатов»: apply what is pending right away
    keep: () => debouncedApply.flush(),
    // Drop a pending change (e.g. «Сбросить всё» replaces it)
    cancel,
    // ✕ / Esc: drop a pending change; true if the session added a history entry
    revert: () => {
      cancel();
      const added = hasEntry;
      hasEntry = false;
      return added;
    },
    // A change applied outside change() (e.g. «Сбросить всё»): push for the first one, replace after
    nextEntryReplaces: () => {
      const replace = hasEntry;
      hasEntry = true;
      return replace;
    },
  };
};
